import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "./AdminShell";
import { AssigneeChipRow, AssigneeEditor } from "../components/AssigneeChip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  in_progress: { label: "In progress", bg: "#FFFBC9", fg: "#68280E", border: "#68280E" },
  live: { label: "Live", bg: "#DFF2E1", fg: "#1C4F2C", border: "#1C4F2C" },
  archived: { label: "Archived", bg: "#EDE6E1", fg: "#5A5757", border: "#8A6B54" },
};

export default function Admin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState("all"); // "all" | "unassigned" | <email>
  const [statusFilter, setStatusFilter] = useState("in_progress"); // "in_progress" | "live" | "archived" | "all"
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignEmails, setBulkAssignEmails] = useState([]);

  const fetchResponses = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? { include_archived: true } : { status: statusFilter };
      const res = await axios.get(`${API}/survey/responses`, { params });
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchResponses(); }, [fetchResponses]);
  useEffect(() => {
    axios.get(`${API}/auth/users`, { withCredentials: true })
      .then((r) => setMembers(r.data || []))
      .catch(() => setMembers([]));
  }, []);

  const directory = useMemo(
    () => Object.fromEntries(members.map((m) => [m.email, m])),
    [members]
  );

  const filteredRows = useMemo(() => {
    if (assigneeFilter === "all") return rows;
    if (assigneeFilter === "unassigned") return rows.filter((r) => !(r.assignees || []).length);
    return rows.filter((r) => (r.assignees || []).includes(assigneeFilter));
  }, [rows, assigneeFilter]);

  const regenerate = async (id) => {
    setRegeneratingId(id);
    try {
      const { data } = await axios.post(`${API}/survey/responses/${id}/regenerate-summary`);
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, persona_summary: data.persona_summary } : r))
      );
      setOpenId(id);
      toast.success("Persona summary refreshed");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't regenerate, please try again.");
    } finally {
      setRegeneratingId(null);
    }
  };

  const right = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ color: "var(--folk-muted)", fontSize: 14 }} data-testid="admin-count">
        {loading ? "Loading…" : `${filteredRows.length} of ${rows.length}`}
      </div>
      <select
        data-testid="status-filter"
        value={statusFilter}
        onChange={(e) => { setStatusFilter(e.target.value); setSelectedIds(new Set()); }}
        className="folk-input"
        style={{ minWidth: 160, padding: "7px 12px", fontSize: 13 }}
      >
        <option value="in_progress">In progress</option>
        <option value="live">Live</option>
        <option value="archived">Archived</option>
        <option value="all">All statuses</option>
      </select>
      <select
        data-testid="assignee-filter"
        value={assigneeFilter}
        onChange={(e) => setAssigneeFilter(e.target.value)}
        className="folk-input"
        style={{ minWidth: 180, padding: "7px 12px", fontSize: 13 }}
      >
        <option value="all">All designers</option>
        <option value="unassigned">Unassigned</option>
        {members.map((m) => (
          <option key={m.email} value={m.email}>{m.name || m.email}</option>
        ))}
      </select>
    </div>
  );

  const setRowStatus = async (row, nextStatus) => {
    if (row.project_status === nextStatus) return;
    setStatusBusyId(row.id);
    try {
      await axios.put(`${API}/survey/responses/${row.id}/status`, { status: nextStatus }, { withCredentials: true });
      toast.success(`Moved to ${STATUS_META[nextStatus]?.label || nextStatus}.`);
      await fetchResponses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update status.");
    } finally {
      setStatusBusyId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((r) => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      filteredRows.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkSetStatus = async (nextStatus) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} project${ids.length === 1 ? "" : "s"} to ${STATUS_META[nextStatus]?.label}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.put(`${API}/survey/responses/bulk/status`, { ids, status: nextStatus }, { withCredentials: true });
      toast.success(`Updated ${data.modified ?? ids.length} project${ids.length === 1 ? "" : "s"}.`);
      clearSelection();
      await fetchResponses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed.");
    } finally { setBulkBusy(false); }
  };

  const bulkAssign = async (mode) => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !bulkAssignEmails.length) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/survey/responses/bulk/assignees`,
        { ids, assignees: bulkAssignEmails, mode },
        { withCredentials: true },
      );
      toast.success(`${mode === "add" ? "Added" : mode === "remove" ? "Removed" : "Set"} assignees on ${data.count} project${data.count === 1 ? "" : "s"}.`);
      setBulkAssignOpen(false);
      setBulkAssignEmails([]);
      clearSelection();
      await fetchResponses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk assign failed.");
    } finally { setBulkBusy(false); }
  };

  return (
    <AdminShell eyebrow="Admin" title="Homeowner responses" right={right}>
      <QuestionsInbox />

      {!loading && rows.length === 0 && (
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 32, textAlign: "center", color: "var(--folk-muted)" }}>
          No submissions yet. Once a homeowner completes the survey, it will appear here.
        </div>
      )}

      {filteredRows.length > 0 && (
        <div
          data-testid="bulk-action-bar"
          style={{
            display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
            background: selectedIds.size ? "#FFFBC9" : "var(--folk-card)",
            border: `1.5px solid ${selectedIds.size ? "#68280E" : "var(--folk-border)"}`,
            borderRadius: 14, padding: "10px 14px", marginBottom: 12,
          }}
        >
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontFamily: "Host Grotesk, sans-serif", cursor: "pointer", fontWeight: 600, color: "var(--folk-accent)" }}>
            <input
              type="checkbox"
              data-testid="bulk-select-all"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              style={{ width: 16, height: 16 }}
            />
            {allVisibleSelected ? "Unselect all" : "Select all"}
          </label>
          <span style={{ fontSize: 13, color: "var(--folk-muted)" }} data-testid="bulk-selected-count">
            {selectedIds.size} selected
          </span>
          {selectedIds.size > 0 && (
            <>
              <div style={{ height: 20, width: 1, background: "var(--folk-border)" }} />
              <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>Set status</span>
              {["in_progress", "live", "archived"].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`bulk-status-${s}`}
                  onClick={() => bulkSetStatus(s)}
                  disabled={bulkBusy}
                  style={{
                    padding: "6px 14px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                    background: STATUS_META[s].bg, color: STATUS_META[s].fg,
                    border: `1.5px solid ${STATUS_META[s].border}`, opacity: bulkBusy ? 0.6 : 1,
                    fontFamily: "Host Grotesk, sans-serif",
                  }}
                >
                  {STATUS_META[s].label}
                </button>
              ))}
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  data-testid="bulk-assign-toggle"
                  onClick={() => setBulkAssignOpen((v) => !v)}
                  disabled={bulkBusy}
                  style={{
                    padding: "6px 14px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                    background: "transparent", color: "var(--folk-accent)",
                    border: "1.5px solid var(--folk-accent)", opacity: bulkBusy ? 0.6 : 1,
                    fontFamily: "Host Grotesk, sans-serif",
                  }}
                >
                  Assign team…
                </button>
                {bulkAssignOpen && (
                  <div
                    data-testid="bulk-assign-panel"
                    style={{
                      position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 30,
                      background: "#FFF", border: "1.5px solid var(--folk-border)", borderRadius: 14,
                      boxShadow: "0 12px 32px rgba(27, 26, 26, 0.12)", width: 280, padding: 12,
                      display: "flex", flexDirection: "column", gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>Assign team to selection</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto" }}>
                      {members.length === 0 && <div style={{ fontSize: 13, color: "var(--folk-muted)" }}>No teammates yet.</div>}
                      {members.map((m) => {
                        const active = bulkAssignEmails.includes(m.email);
                        return (
                          <label
                            key={m.email}
                            data-testid={`bulk-assign-option-${m.email}`}
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "6px 8px", borderRadius: 8, cursor: "pointer",
                              background: active ? "#FFFBC9" : "transparent",
                              fontFamily: "Host Grotesk, sans-serif", fontSize: 13,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => setBulkAssignEmails((cur) => cur.includes(m.email) ? cur.filter((e) => e !== m.email) : [...cur, m.email])}
                              style={{ width: 16, height: 16 }}
                            />
                            {m.name || m.email}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                      <button type="button" data-testid="bulk-assign-add" onClick={() => bulkAssign("add")} disabled={bulkBusy || !bulkAssignEmails.length} className="folk-pill" style={{ fontSize: 12, padding: "6px 12px", opacity: (bulkBusy || !bulkAssignEmails.length) ? 0.5 : 1 }}>Add</button>
                      <button type="button" data-testid="bulk-assign-replace" onClick={() => bulkAssign("replace")} disabled={bulkBusy || !bulkAssignEmails.length} className="folk-pill" style={{ fontSize: 12, padding: "6px 12px", background: "transparent", color: "var(--folk-accent)", border: "1.5px solid var(--folk-accent)", opacity: (bulkBusy || !bulkAssignEmails.length) ? 0.5 : 1 }}>Replace</button>
                      <button type="button" data-testid="bulk-assign-remove" onClick={() => bulkAssign("remove")} disabled={bulkBusy || !bulkAssignEmails.length} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 999, background: "transparent", color: "#B04A2E", border: "1.5px solid #B04A2E", cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", opacity: (bulkBusy || !bulkAssignEmails.length) ? 0.5 : 1 }}>Remove</button>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                data-testid="bulk-clear"
                onClick={clearSelection}
                style={{ marginLeft: "auto", background: "transparent", border: "none", color: "var(--folk-muted)", fontSize: 12.5, cursor: "pointer", fontFamily: "Host Grotesk, sans-serif" }}
              >
                Clear selection
              </button>
            </>
          )}
        </div>
      )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filteredRows.map((r) => {
            const open = openId === r.id;
            const answers = r.answers || {};
            const preview =
              answers["Who lives in the home"] ||
              answers["Preferred styles"] ||
              answers["Five words"] ||
              "–";
            return (
              <div
                key={r.id}
                style={{
                  background: "var(--folk-card)",
                  border: `1.5px solid ${selectedIds.has(r.id) ? "#68280E" : "var(--folk-border)"}`,
                  borderRadius: 18,
                  padding: 20,
                  display: "flex",
                  gap: 14,
                  alignItems: "flex-start",
                }}
                data-testid={`response-row-${r.id}`}
              >
                <input
                  type="checkbox"
                  data-testid={`row-select-${r.id}`}
                  checked={selectedIds.has(r.id)}
                  onChange={(e) => { e.stopPropagation(); toggleSelect(r.id); }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: 18, height: 18, marginTop: 4, cursor: "pointer", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, cursor: "pointer" }}
                  onClick={() => setOpenId(open ? null : r.id)}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 18 }}>
                        {r.contact_name || "Anonymous homeowner"}
                      </div>
                      <StatusPill
                        row={r}
                        busy={statusBusyId === r.id}
                        onChange={(next) => setRowStatus(r, next)}
                      />
                    </div>
                    <div style={{ fontSize: 13, color: "var(--folk-muted)", marginTop: 4 }}>
                      {new Date(r.submitted_at).toLocaleString()}
                      {r.contact_email ? ` · ${r.contact_email}` : ""}
                      {" · "}{Object.keys(answers).length} answers
                    </div>
                    {r.updated_at && (
                      <div data-testid={`updated-${r.id}`} style={{ fontSize: 12.5, marginTop: 4, color: r.updated_by === "homeowner" ? "#0F3A54" : "var(--folk-muted)", fontWeight: r.updated_by === "homeowner" ? 600 : 400 }}>
                        {r.updated_by === "homeowner" ? "✎ Homeowner" : "✎ Admin"} edited {new Date(r.updated_at).toLocaleString()}
                      </div>
                    )}
                    <div style={{ fontSize: 14, color: "#2A2828", marginTop: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                      {typeof preview === "string" ? preview : String(preview)}
                    </div>
                    <div data-testid={`row-assignees-${r.id}`} style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
                      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600 }}>Owner</span>
                      <AssigneeEditor
                        submissionId={r.id}
                        value={r.assignees || []}
                        onChanged={(next) => {
                          setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, assignees: next } : x));
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                    <Link
                      to={`/admin/submissions/${r.id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`manage-${r.id}`}
                      style={{
                        fontSize: 14,
                        color: "#FFF",
                        background: "#68280E",
                        whiteSpace: "nowrap",
                        padding: "8px 16px",
                        border: "1.5px solid #68280E",
                        borderRadius: 999,
                        textDecoration: "none",
                        fontWeight: 600,
                        fontFamily: "Host Grotesk, sans-serif",
                      }}
                    >
                      Manage →
                    </Link>
                    <button
                      type="button"
                      data-testid={`regen-${r.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        regenerate(r.id);
                      }}
                      disabled={regeneratingId === r.id}
                      style={{
                        fontSize: 14,
                        color: "var(--folk-accent)",
                        background: "transparent",
                        whiteSpace: "nowrap",
                        padding: "7px 13px",
                        border: "1.5px solid var(--folk-accent)",
                        borderRadius: 999,
                        cursor: regeneratingId === r.id ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        opacity: regeneratingId === r.id ? 0.6 : 1,
                        fontFamily: "Host Grotesk, sans-serif",
                      }}
                    >
                      {regeneratingId === r.id ? "Regenerating…" : "Regenerate"}
                    </button>
                    <a
                      href={`${API}/survey/responses/${r.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`pdf-${r.id}`}
                      style={{
                        fontSize: 14,
                        color: "var(--folk-card)",
                        background: "var(--folk-accent)",
                        whiteSpace: "nowrap",
                        padding: "8px 14px",
                        borderRadius: 999,
                        textDecoration: "none",
                        fontWeight: 600,
                      }}
                    >
                      Persona PDF
                    </a>
                    <a
                      href={`${API}/portal/${r.id}/docket-pdf`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`docket-${r.id}`}
                      style={{
                        fontSize: 14,
                        color: "var(--folk-accent)",
                        background: "transparent",
                        whiteSpace: "nowrap",
                        padding: "7px 13px",
                        border: "1.5px solid var(--folk-accent)",
                        borderRadius: 999,
                        textDecoration: "none",
                        fontWeight: 600,
                      }}
                    >
                      Docket PDF
                    </a>
                    <Link
                      to={`/survey?edit=${r.id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`edit-answers-${r.id}`}
                      style={{
                        fontSize: 14,
                        color: "var(--folk-accent)",
                        background: "transparent",
                        whiteSpace: "nowrap",
                        padding: "7px 13px",
                        border: "1.5px solid var(--folk-accent)",
                        borderRadius: 999,
                        textDecoration: "none",
                        fontWeight: 600,
                      }}
                    >
                      Edit answers
                    </Link>
                    <a
                      href={`/portal/${r.id}?asAdmin=1`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`view-portal-${r.id}`}
                      style={{
                        fontSize: 14,
                        color: "var(--folk-accent)",
                        background: "transparent",
                        whiteSpace: "nowrap",
                        padding: "7px 13px",
                        border: "1.5px solid var(--folk-accent)",
                        borderRadius: 999,
                        textDecoration: "none",
                        fontWeight: 600,
                      }}
                    >
                      Open portal ↗
                    </a>
                    {r.advance_paid && (
                      <span
                        data-testid={`paid-badge-${r.id}`}
                        style={{ fontSize: 11.5, letterSpacing: "0.12em", padding: "6px 10px", borderRadius: 999, background: "#1F7A4C", color: "#FFFFFF", textTransform: "uppercase", fontWeight: 600, whiteSpace: "nowrap" }}
                        title={r.paid_tier?.name ? `Paid — ${r.paid_tier.name}` : "Advance paid"}
                      >
                        🟢 Paid — start Pinterest
                      </span>
                    )}
                    <div
                      style={{
                        fontSize: 14,
                        color: "var(--folk-accent)",
                        whiteSpace: "nowrap",
                        padding: "6px 12px",
                        border: "1.5px solid var(--folk-border)",
                        borderRadius: 999,
                      }}
                      data-testid={`toggle-${r.id}`}
                    >
                      {open ? "Hide" : "View"}
                    </div>
                  </div>
                </div>

                {open && (
                  <div style={{ marginTop: 18, borderTop: "1px solid var(--folk-border)", paddingTop: 18, display: "flex", flexDirection: "column", gap: 18 }}>
                    <ProgressPanel submission={r} onUpdated={fetchResponses} />
                    <PricingKickstartPanel submission={r} onUpdated={fetchResponses} />
                    {r.persona_summary && (
                      <div
                        data-testid={`summary-${r.id}`}
                        style={{
                          background: "#BADDF0",
                          border: "1px solid rgba(27, 74, 102, 0.2)",
                          borderRadius: 12,
                          padding: 16,
                        }}
                      >
                        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 600, marginBottom: 8 }}>
                          A note from the design team
                        </div>
                        <div style={{ fontSize: 14.5, lineHeight: 1.6, color: "#0F3A54", whiteSpace: "pre-wrap" }}>
                          {r.persona_summary}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
                      {Object.entries(answers).map(([k, v]) => (
                        <div key={k} style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
                          <div style={{ color: "var(--folk-hint)", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.04em" }}>{k}</div>
                          <div style={{ fontSize: 15, color: "#2A2828", whiteSpace: "pre-wrap" }}>
                            {Array.isArray(v) ? v.join(", ") : String(v)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              </div>
            );
          })}
        </div>
    </AdminShell>
  );
}

function StatusPill({ row, busy, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const current = row.project_status || (row.archived ? "archived" : "in_progress");
  const meta = STATUS_META[current] || STATUS_META.in_progress;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        data-testid={`row-status-${row.id}`}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        style={{
          padding: "4px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: busy ? "wait" : "pointer",
          background: meta.bg, color: meta.fg, border: `1.5px solid ${meta.border}`,
          textTransform: "uppercase", letterSpacing: "0.1em", opacity: busy ? 0.6 : 1,
          fontFamily: "Host Grotesk, sans-serif",
        }}
      >
        {busy ? "…" : meta.label}
      </button>
      {open && (
        <div
          data-testid={`row-status-menu-${row.id}`}
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20,
            background: "#FFF", border: "1.5px solid var(--folk-border)", borderRadius: 12,
            boxShadow: "0 12px 32px rgba(27, 26, 26, 0.12)", padding: 6, display: "flex", flexDirection: "column",
            minWidth: 140,
          }}
        >
          {["in_progress", "live", "archived"].map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`row-status-option-${row.id}-${s}`}
              onClick={() => { setOpen(false); onChange(s); }}
              style={{
                textAlign: "left", padding: "8px 12px", borderRadius: 8, border: "none",
                background: current === s ? "#FFFBC9" : "transparent",
                color: STATUS_META[s].fg, fontWeight: current === s ? 700 : 500,
                fontSize: 13, cursor: "pointer", fontFamily: "Host Grotesk, sans-serif",
              }}
            >
              {STATUS_META[s].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionsInbox() {
  const [queries, setQueries] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("open"); // "open" | "replied"
  const [digestStatus, setDigestStatus] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [rateCardUrl, setRateCardUrl] = useState("");

  const load = React.useCallback(async () => {
    try {
      const [qRes, dRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/queries`),
        axios.get(`${API}/admin/queries/digest/status`).catch(() => ({ data: null })),
        axios.get(`${API}/content`).catch(() => ({ data: null })),
      ]);
      setQueries(qRes.data || []);
      if (dRes.data) setDigestStatus(dRes.data);
      if (cRes.data?.rate_card?.pdf_url) setRateCardUrl(cRes.data.rate_card.pdf_url);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/queries/digest/send`);
      if (data.open_count === 0) {
        toast.success("Nothing to send — inbox is empty. Great job!");
      } else if (data.sent) {
        toast.success(`Digest sent (${data.open_count} open message${data.open_count !== 1 ? 's' : ''}).`);
      } else {
        toast.warning("Digest ran but Resend isn't configured — no email sent.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the digest.");
    } finally { setDigestBusy(false); }
  };

  const setDraft = (id, val) => setReplyDrafts((d) => ({ ...d, [id]: val }));

  const openCount = queries.filter((q) => q.status === "open").length;
  const filtered = queries.filter((q) => q.status === tab);

  const isRateCardRequest = (q) => (q?.subject || "").toLowerCase().includes("rate card");

  const expandQuery = (q) => {
    if (expanded === q.id) {
      setExpanded(null);
      return;
    }
    setExpanded(q.id);
    // Prefill a reply template for rate card requests so the team never
    // has to paste the current PDF link manually.
    if (q.status === "open" && isRateCardRequest(q) && !replyDrafts[q.id] && rateCardUrl) {
      const name = (q.contact_name || "").split(" ")[0] || "there";
      const template = `Hi ${name},\n\nAs requested, here's the latest Folk rate card:\n${rateCardUrl}\n\nHappy to jump on a quick call to walk you through it — just say the word.\n\nWarmly,\nThe Folk team`;
      setReplyDrafts((d) => ({ ...d, [q.id]: template }));
    }
  };

  const send = async (id) => {
    const reply = (replyDrafts[id] || "").trim();
    if (!reply) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/queries/${id}/reply`, { reply });
      setDraft(id, "");
      setExpanded(null);
      toast.success("Reply sent — homeowner will get an email.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send reply.");
    } finally { setBusyId(null); }
  };

  if (queries.length === 0 && !digestStatus?.last_run_at) return null;

  return (
    <div data-testid="questions-inbox" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 16, marginBottom: 18, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="questions-toggle"
        style={{ width: "100%", background: openCount ? "#FFFBC9" : "var(--folk-card)", border: "none", padding: "14px 18px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "Host Grotesk, sans-serif" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>Homeowner messages</div>
          {openCount > 0 && (
            <span data-testid="questions-count" style={{ background: "#68280E", color: "#FFF", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{openCount} awaiting reply</span>
          )}
        </div>
        <div style={{ fontSize: 13, color: "var(--folk-muted)" }}>{open ? "Hide" : "Open"}</div>
      </button>
      {open && (
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { key: "open", label: `Awaiting reply (${queries.filter((q) => q.status === "open").length})` },
                { key: "replied", label: `Replied (${queries.filter((q) => q.status === "replied").length})` },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`queries-tab-${t.key}`}
                  onClick={() => { setTab(t.key); setExpanded(null); }}
                  style={{
                    border: "1.5px solid var(--folk-border)",
                    background: tab === t.key ? "#68280E" : "#FFF",
                    color: tab === t.key ? "#FFF" : "var(--folk-text)",
                    borderRadius: 999, padding: "6px 14px", fontSize: 12,
                    fontFamily: "Host Grotesk, sans-serif", fontWeight: 600, cursor: "pointer",
                  }}
                >{t.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {digestStatus?.last_sent_at && (
                <span style={{ fontSize: 11, color: "var(--folk-muted)", fontFamily: "Host Grotesk, sans-serif" }}>
                  Last digest: {new Date(digestStatus.last_sent_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </span>
              )}
              <button
                type="button"
                data-testid="queries-send-digest"
                onClick={sendDigest}
                disabled={digestBusy}
                className="folk-pill"
                style={{ padding: "6px 14px", fontSize: 12, background: "#FFF", color: "#68280E", border: "1.5px solid #68280E" }}
                title="Emails the team a summary of every open message + a WhatsApp share link"
              >
                {digestBusy ? "Sending…" : "Send digest now"}
              </button>
            </div>
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: "12px 4px", color: "var(--folk-muted)", fontSize: 14 }}>
              {tab === "open" ? "No messages waiting on a reply — you're all caught up." : "No replied messages yet."}
            </div>
          )}
          {filtered.map((q) => (
            <div key={q.id} data-testid={`query-row-${q.id}`} style={{ background: "var(--folk-bg)", border: "1px solid var(--folk-border)", borderRadius: 12, overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => expandQuery(q)}
                data-testid={`query-toggle-${q.id}`}
                style={{ width: "100%", background: "transparent", border: "none", cursor: "pointer", padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, textAlign: "left", fontFamily: "Host Grotesk, sans-serif" }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1B1A1A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{q.subject}</div>
                    {isRateCardRequest(q) && (
                      <span data-testid={`rate-card-tag-${q.id}`} style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, background: "#FFFBC9", color: "#68280E", borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>rate card</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--folk-muted)" }}>{q.contact_name || q.contact_email}</div>
                </div>
                <span style={{ fontSize: 12, color: "var(--folk-hint)", whiteSpace: "nowrap" }}>{new Date(q.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
              </button>
              {expanded === q.id && (
                <div style={{ padding: 14, borderTop: "1px solid var(--folk-border)", background: "#FFF", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--folk-muted)", fontWeight: 700, marginBottom: 4 }}>Message</div>
                    <div style={{ background: "#FAF5F3", borderRadius: 10, padding: "10px 12px", fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "#1B1A1A" }}>{q.message}</div>
                  </div>
                  {q.status === "replied" && q.admin_reply && (
                    <div>
                      <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700, marginBottom: 4 }}>Your reply</div>
                      <div style={{ background: "#FFFBC9", borderRadius: 10, padding: "10px 12px", fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "#1B1A1A" }}>{q.admin_reply}</div>
                      <div style={{ fontSize: 11, color: "var(--folk-hint)", marginTop: 4 }}>Sent {new Date(q.replied_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} by {q.replied_by}</div>
                    </div>
                  )}
                  {q.status === "open" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <textarea
                        data-testid={`query-reply-${q.id}`}
                        value={replyDrafts[q.id] || ""}
                        onChange={(e) => setDraft(q.id, e.target.value)}
                        placeholder="Reply as the Folk team — this gets emailed to the homeowner."
                        className="folk-input"
                        rows={4}
                        style={{ padding: "10px 14px", fontSize: 14, resize: "vertical", fontFamily: "Host Grotesk, sans-serif" }}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                          type="button"
                          data-testid={`query-send-${q.id}`}
                          className="folk-pill"
                          disabled={busyId === q.id || !(replyDrafts[q.id] || "").trim()}
                          onClick={() => send(q.id)}
                          style={{ padding: "10px 16px", fontSize: 14 }}
                        >
                          {busyId === q.id ? "Sending…" : "Send reply"}
                        </button>
                      </div>
                    </div>
                  )}
                  <a href={`/portal/${q.submission_id}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "var(--folk-accent)", textDecoration: "underline" }}>Open portal ↗</a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PHASES = [
  { key: "discovery", label: "Discovery" },
  { key: "concept", label: "Concept" },
  { key: "design_dev", label: "Design Development" },
  { key: "sourcing", label: "Sourcing" },
  { key: "install", label: "Install" },
  { key: "handover", label: "Handover" },
];

function PinterestPanel({ submission, onUpdated }) {
  const [url, setUrl] = useState(submission.pinterest_url || "");
  const [busy, setBusy] = useState(false);
  const paidWithoutBoard = submission.advance_paid && !submission.pinterest_url;
  const suggestedTitle = `Folk × ${(submission.contact_name || "Client").trim()}`;

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/survey/responses/${submission.id}/pinterest`, { url: url.trim() });
      toast.success(url.trim() ? "Board saved. Client sees it on Moodboard tab." : "Cleared Pinterest board.");
      onUpdated?.();
    } catch {
      toast.error("Couldn't save the URL, check the format.");
    } finally {
      setBusy(false);
    }
  };

  const copyTitle = async () => {
    try {
      await navigator.clipboard.writeText(suggestedTitle);
      toast.success("Board title copied to clipboard.");
    } catch {
      toast.error("Couldn't copy, please copy it manually.");
    }
  };

  return (
    <div data-testid={`pinterest-panel-${submission.id}`} style={{ background: paidWithoutBoard ? "#FFFBC9" : "var(--folk-bg)", border: paidWithoutBoard ? "2px solid var(--folk-accent)" : "1.5px solid var(--folk-border)", borderRadius: 14, padding: 18 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600, marginBottom: 12 }}>
        Shared Pinterest board
      </div>

      {paidWithoutBoard && (
        <div data-testid={`pinterest-kickoff-${submission.id}`} style={{ marginBottom: 14, padding: 14, background: "#FFFFFF", borderRadius: 12, border: "1.5px solid var(--folk-border)" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#68280E" }}>
            🎨 Time to launch this client's board
          </div>
          <div style={{ marginTop: 6, fontSize: 13.5, color: "#2A2828", lineHeight: 1.55 }}>
            Their advance is in. Create a private Pinterest board titled below, seed it with the persona references, then paste the shared URL here so it appears on their Moodboard tab.
          </div>
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <div data-testid={`pinterest-suggested-${submission.id}`} style={{ padding: "8px 14px", background: "#FAF5F3", borderRadius: 8, fontFamily: "'Playfair Display', serif", fontSize: 16 }}>
              {suggestedTitle}
            </div>
            <button
              type="button"
              data-testid={`pinterest-copy-${submission.id}`}
              onClick={copyTitle}
              className="folk-btn"
            >
              Copy title
            </button>
            <a
              href="https://www.pinterest.com/board-picker/save/"
              target="_blank"
              rel="noreferrer"
              data-testid={`pinterest-open-${submission.id}`}
              className="folk-pill"
              style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              Open Pinterest ↗
            </a>
          </div>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "start" }}>
        <input
          data-testid={`pinterest-url-${submission.id}`}
          type="url"
          placeholder="https://www.pinterest.com/your-user/board-slug/"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="folk-input"
        />
        <button
          type="button"
          data-testid={`pinterest-save-${submission.id}`}
          className="folk-pill"
          disabled={busy}
          onClick={save}
          style={{ height: 46, padding: "0 22px" }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
      <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--folk-muted)" }}>
        Paste any public Pinterest board URL, it appears embedded on the client's Moodboard tab. Leave blank to remove.
      </div>
    </div>
  );
}

function ProgressPanel({ submission, onUpdated }) {
  const [phase, setPhase] = useState(submission.current_phase || "discovery");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const updates = Array.isArray(submission.progress_updates) ? [...submission.progress_updates].reverse() : [];

  const save = async () => {
    if (!phase && !note.trim()) {
      toast.error("Pick a phase or write a note first");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/survey/responses/${submission.id}/progress`, { phase, note: note.trim() || null });
      setNote("");
      toast.success("Progress posted. Client sees it within 30 seconds");
      onUpdated?.();
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save, please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`progress-panel-${submission.id}`} style={{ background: "var(--folk-bg)", border: "1.5px solid var(--folk-border)", borderRadius: 14, padding: 18 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600, marginBottom: 12 }}>
        Update project progress
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: 12, alignItems: "start" }}>
        <select
          data-testid={`progress-phase-${submission.id}`}
          value={phase}
          onChange={(e) => setPhase(e.target.value)}
          className="folk-input"
          style={{ height: 46 }}
        >
          {PHASES.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
        <textarea
          data-testid={`progress-note-${submission.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note the client will see (e.g. 'Concept board ready, check the moodboard tab')"
          rows={2}
          className="folk-textarea"
          style={{ minHeight: 46 }}
        />
        <button
          type="button"
          data-testid={`progress-save-${submission.id}`}
          className="folk-pill"
          disabled={busy}
          onClick={save}
          style={{ height: 46, padding: "0 22px" }}
        >
          {busy ? "Saving…" : "Post"}
        </button>
      </div>
      {updates.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto" }}>
          {updates.map((u) => (
            <div key={u.id} style={{ background: "var(--folk-card)", border: "1px solid var(--folk-border)", borderRadius: 10, padding: "10px 12px", fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--folk-hint)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
                <span>{PHASES.find((p) => p.key === u.phase)?.label || u.phase || "Note"}</span>
                <span>{u.author}</span>
              </div>
              <div style={{ color: "#2A2828", whiteSpace: "pre-wrap" }}>{u.note}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------- Pricing + Kickstart Panel ---------- */
function PricingKickstartPanel({ submission, onUpdated }) {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("");
  const [recommendedTier, setRecommendedTier] = useState(submission.recommended_tier_id || "");
  const [autoSuggested, setAutoSuggested] = useState({ id: "", name: "" });
  const [savingRecommendation, setSavingRecommendation] = useState(false);
  const [sending, setSending] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [sentAt, setSentAt] = useState(submission.pricing_email_sent_at || null);
  const [reportReadySentAt, setReportReadySentAt] = useState(submission.report_ready_sent_at || null);
  const [reportReadyBusy, setReportReadyBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tRes, rRes] = await Promise.all([
          axios.get(`${API}/pricing/tiers`),
          axios.get(`${API}/portal/${submission.id}/report`),
        ]);
        const list = tRes.data || [];
        setTiers(list);
        const rec = list.find((t) => t.is_recommended) || list[0];
        if (rec) setSelectedTier(rec.id);
        const autoId = rRes.data?.auto_suggested_tier_id || "";
        const autoName = rRes.data?.auto_suggested_tier_name || "";
        setAutoSuggested({ id: autoId, name: autoName });
        // Default the recommendation to the auto-suggest when admin hasn't picked yet.
        if (!submission.recommended_tier_id && autoId) {
          setRecommendedTier(autoId);
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission.id]);

  const applyAutoSuggestion = async () => {
    if (!autoSuggested.id) return;
    await saveRecommendation(autoSuggested.id);
  };

  const saveRecommendation = async (newId) => {
    setRecommendedTier(newId);
    setSavingRecommendation(true);
    try {
      await axios.put(
        `${API}/survey/responses/${submission.id}/report-config`,
        { recommended_tier_id: newId || null },
        { withCredentials: true },
      );
      toast.success("Recommendation saved.");
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the recommendation.");
    } finally { setSavingRecommendation(false); }
  };

  const sendReportReady = async () => {
    if (!submission.contact_email) {
      toast.error("No email on this submission, can't send.");
      return;
    }
    setReportReadyBusy(true);
    try {
      const res = await axios.post(
        `${API}/survey/responses/${submission.id}/send-report-ready`,
        {},
        { withCredentials: true }
      );
      setReportReadySentAt(res.data?.sent_at || new Date().toISOString());
      const pins = res.data?.pin_count ?? 0;
      const parts = ["Report-ready email sent"];
      if (pins > 0) parts.push(`with ${pins} moodboard pin${pins !== 1 ? 's' : ''} attached`);
      toast.success(parts.join(" ") + ".");
      if (res.data?.email_id === null) {
        toast.warning("Resend isn't configured — email logged but not delivered.");
      }
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the report-ready email.");
    } finally {
      setReportReadyBusy(false);
    }
  };

  const sendReport = async () => {
    if (!submission.contact_email) {
      toast.error("No email on this submission, can't send.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/survey/responses/${submission.id}/send-pricing`,
        {},
        { withCredentials: true }
      );
      setSentAt(new Date().toISOString());
      toast.success("Report + rate card email sent.");
      if (res.data?.email_id === null) {
        toast.warning("Email service isn't configured, but the pricing link is saved.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the email.");
    } finally {
      setSending(false);
    }
  };

  const [briefBusy, setBriefBusy] = useState(false);
  const downloadMoodboardBrief = async () => {
    setBriefBusy(true);
    try {
      const res = await axios.post(
        `${API}/survey/responses/${submission.id}/moodboard-brief`,
        {},
        { withCredentials: true, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const safe = (submission.contact_name || "homeowner").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const a = document.createElement("a");
      a.href = url;
      a.download = `folk-objects-moodboard-brief-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Moodboard brief ready — check downloads.");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Couldn't generate the brief.";
      toast.error(detail);
    } finally {
      setBriefBusy(false);
    }
  };

  const simulatePaid = async () => {
    if (!selectedTier) {
      toast.error("Pick a tier first.");
      return;
    }
    if (!window.confirm("Mark advance as paid and kick off the project?")) return;
    setSimulating(true);
    try {
      await axios.post(
        `${API}/survey/responses/${submission.id}/simulate-payment`,
        { tier_id: selectedTier },
        { withCredentials: true }
      );
      toast.success("Advance recorded, project kicked off.");
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't record payment.");
    } finally {
      setSimulating(false);
    }
  };

  const pricingUrl = `${window.location.origin}/portal/${submission.id}/report`;

  return (
    <div
      data-testid={`pricing-panel-${submission.id}`}
      style={{ background: "#FFFFFF", border: "1.5px solid var(--folk-border)", borderRadius: 12, padding: 16 }}
    >
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600 }}>
        Report & Pricing
      </div>
      {submission.advance_paid ? (
        <div style={{ marginTop: 10, fontSize: 14, color: "#1F7A4C", fontWeight: 600 }}>
          🟢 Advance paid ({submission.paid_tier?.name || "tier"}). Project is live.
        </div>
      ) : (
        <>
          <div style={{ marginTop: 12, padding: "10px 12px", background: "#FFFBC9", borderRadius: 10, border: "1px solid rgba(104, 40, 14, 0.18)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700 }}>Recommended plan for this homeowner</div>
              {autoSuggested.name && (
                <div data-testid={`auto-suggest-${submission.id}`} style={{ fontSize: 11, color: "#5A5757", fontFamily: "Host Grotesk, sans-serif" }}>
                  Auto-suggested from survey: <strong>{autoSuggested.name}</strong>
                  {autoSuggested.id && recommendedTier !== autoSuggested.id && (
                    <button
                      type="button"
                      data-testid={`apply-auto-${submission.id}`}
                      onClick={applyAutoSuggestion}
                      disabled={savingRecommendation}
                      style={{ marginLeft: 8, background: "transparent", border: "none", color: "#68280E", cursor: "pointer", textDecoration: "underline", fontSize: 11 }}
                    >
                      Use this
                    </button>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select
                data-testid={`recommend-tier-${submission.id}`}
                value={recommendedTier}
                onChange={(e) => saveRecommendation(e.target.value)}
                disabled={savingRecommendation}
                className="folk-input"
                style={{ maxWidth: 320 }}
              >
                <option value="">No recommendation</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} — {t.price_display}</option>
                ))}
              </select>
              {savingRecommendation && <span style={{ fontSize: 12, color: "var(--folk-muted)" }}>Saving…</span>}
            </div>
            <div style={{ fontSize: 12, color: "#5A5757", marginTop: 6, lineHeight: 1.5 }}>
              This highlights the plan on the homeowner's report page and in the email.
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 13.5, color: "var(--folk-muted)", lineHeight: 1.5 }}>
            Sends the persona summary + rate card via email to <b>{submission.contact_email || "no email set"}</b>.
            Homeowner's report:{" "}
            <a href={pricingUrl} target="_blank" rel="noreferrer" data-testid={`pricing-url-${submission.id}`} style={{ color: "var(--folk-accent)" }}>{pricingUrl}</a>
          </div>
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <button
              data-testid={`send-pricing-${submission.id}`}
              onClick={sendReport}
              disabled={sending || !submission.contact_email}
              className="folk-pill"
              style={{ opacity: (sending || !submission.contact_email) ? 0.55 : 1 }}
            >
              {sending ? "Sending…" : sentAt ? "Resend report + pricing" : "Send report & pricing"}
            </button>
            {sentAt && <span style={{ fontSize: 12, color: "var(--folk-muted)" }}>Last sent {new Date(sentAt).toLocaleString()}</span>}
          </div>

          <div style={{ marginTop: 14, padding: "12px 14px", background: "#BADDF0", borderRadius: 12, border: "1.5px solid rgba(15, 58, 84, 0.24)" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700, marginBottom: 6 }}>Report is live · Notification email</div>
            <div style={{ fontSize: 13, color: "#0F3A54", lineHeight: 1.55, marginBottom: 10 }}>
              A warm "thank you, your report is live" email to <strong>{submission.contact_email || "no email set"}</strong>, with the persona brief PDF (palette, textures, do's & don'ts, homeowner photos) attached. Grab the moodboard brief PDF below to hunt pins with a Claude-generated shot list.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <button
                type="button"
                data-testid={`send-report-ready-${submission.id}`}
                onClick={sendReportReady}
                disabled={reportReadyBusy || !submission.contact_email}
                className="folk-pill"
                style={{
                  background: "#0F3A54", color: "#FFFBC9", border: "1.5px solid #0F3A54",
                  opacity: (reportReadyBusy || !submission.contact_email) ? 0.55 : 1,
                  padding: "10px 18px", fontSize: 13,
                }}
              >
                {reportReadyBusy ? "Sending…" : reportReadySentAt ? "Resend report-ready email" : "Send report-ready email"}
              </button>
              <a
                href={`/portal/${submission.id}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`open-portal-${submission.id}`}
                style={{ fontSize: 12, color: "#0F3A54", textDecoration: "underline", fontFamily: "Host Grotesk, sans-serif" }}
              >
                Open portal to curate moodboard →
              </a>
              <button
                type="button"
                data-testid={`download-moodboard-brief-${submission.id}`}
                onClick={downloadMoodboardBrief}
                disabled={briefBusy}
                className="folk-pill"
                style={{
                  background: "#FFFBC9", color: "#0F3A54", border: "1.5px solid #0F3A54",
                  padding: "10px 16px", fontSize: 12.5,
                  opacity: briefBusy ? 0.55 : 1,
                }}
              >
                {briefBusy ? "Preparing brief…" : "Download moodboard brief (PDF)"}
              </button>
              {reportReadySentAt && <span style={{ fontSize: 12, color: "#0F3A54", opacity: 0.75 }}>Last sent {new Date(reportReadySentAt).toLocaleString()}</span>}
            </div>
          </div>

          <div style={{ marginTop: 18, borderTop: "1px dashed var(--folk-border)", paddingTop: 14 }}>
            <div style={{ fontSize: 12.5, color: "var(--folk-muted)", marginBottom: 6 }}>
              Simulate an advance payment for testing (until Razorpay is wired):
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select
                data-testid={`select-tier-${submission.id}`}
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="folk-input"
                style={{ maxWidth: 260 }}
              >
                <option value="">Pick a tier…</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} — {t.price_display}</option>
                ))}
              </select>
              <button
                data-testid={`simulate-pay-${submission.id}`}
                onClick={simulatePaid}
                disabled={simulating || !selectedTier}
                className="folk-btn"
                style={{ opacity: (simulating || !selectedTier) ? 0.55 : 1 }}
              >
                {simulating ? "Recording…" : "Simulate advance paid"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

